/**
 * ArbitratorKYC.jsx — Professional profile & KYC submission for ARBITRATOR role.
 *
 * Arbitrators submit:
 *  - Personal details (name, phone, city)
 *  - Professional qualifications (degree, years of experience, specialization)
 *  - Registration numbers (bar council, arbitration council)
 *  - Document uploads (govt ID, qualification cert, arbitration cert)
 *
 * Admin manually reviews and approves/rejects.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  FormControl, InputLabel, MenuItem, Paper, Select,
  TextField, Typography,
} from '@mui/material';
import GavelIcon           from '@mui/icons-material/Gavel';
import UploadFileIcon      from '@mui/icons-material/UploadFile';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon  from '@mui/icons-material/HourglassEmpty';
import CancelIcon          from '@mui/icons-material/Cancel';
import AccountCircleIcon   from '@mui/icons-material/AccountCircle';
import { apiClient }       from '../../utils/auth';

const SPECIALIZATIONS = [
  { value: 'CYBER_SECURITY',        label: 'Cyber Security Disputes' },
  { value: 'IT_CONTRACTS',          label: 'IT Contracts & SLAs' },
  { value: 'DATA_PRIVACY',          label: 'Data Privacy & DPDP Act' },
  { value: 'INTELLECTUAL_PROPERTY', label: 'Intellectual Property' },
  { value: 'GENERAL_COMMERCIAL',    label: 'General Commercial Disputes' },
  { value: 'OTHER',                 label: 'Other' },
];

const ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

const STATUS_CONFIG = {
  NOT_SUBMITTED: { color: 'default',  icon: null,                  label: 'Not Submitted',   bg: 'rgba(100,116,139,0.08)' },
  PENDING:       { color: 'warning',  icon: <HourglassEmptyIcon />, label: 'Under Review',    bg: 'rgba(245,158,11,0.08)' },
  APPROVED:      { color: 'success',  icon: <CheckCircleIcon />,    label: 'Approved',        bg: 'rgba(16,185,129,0.08)' },
  REJECTED:      { color: 'error',    icon: <CancelIcon />,         label: 'Rejected',        bg: 'rgba(239,68,68,0.08)'  },
};

export default function ArbitratorKYC() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState('');
  const [success, setSuccess]   = useState('');

  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    city: '',
    qualification: '',
    years_of_experience: '',
    specialization: 'CYBER_SECURITY',
    bar_council_number: '',
    arbitration_council_number: '',
    professional_bio: '',
  });

  const [files, setFiles] = useState({
    govt_id: null,
    qualification: null,
    certification: null,
  });
  const [fileErrors, setFileErrors] = useState({});

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/v1/kyc/arbitrator/profile/');
      setProfile(data);
      if (data.verification_status !== 'NOT_SUBMITTED') {
        setForm({
          full_name:                   data.full_name || '',
          phone:                       data.phone || '',
          city:                        data.city || '',
          qualification:               data.qualification || '',
          years_of_experience:         data.years_of_experience ?? '',
          specialization:              data.specialization || 'CYBER_SECURITY',
          bar_council_number:          data.bar_council_number || '',
          arbitration_council_number:  data.arbitration_council_number || '',
          professional_bio:            data.professional_bio || '',
        });
      }
    } catch {
      setProfile({ verification_status: 'NOT_SUBMITTED' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const validateFile = (file, key) => {
    if (!file) return null;
    if (file.size > MAX_SIZE) return 'File must be under 5 MB.';
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type))
      return 'Only PDF, JPG, or PNG files are allowed.';
    return null;
  };

  const handleFileChange = (key, file) => {
    const err = validateFile(file, key);
    setFileErrors(prev => ({ ...prev, [key]: err }));
    if (!err) setFiles(prev => ({ ...prev, [key]: file }));
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');

    if (!form.full_name.trim() || !form.qualification.trim()) {
      setError('Full name and qualification are required.');
      return;
    }

    setSaving(true);
    try {
      // Build form data for file uploads
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.govt_id)      fd.append('govt_id_file',      files.govt_id);
      if (files.qualification) fd.append('qualification_file', files.qualification);
      if (files.certification) fd.append('certification_file', files.certification);

      await apiClient.post('/api/v1/kyc/arbitrator/profile/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Your profile has been submitted successfully. Admin will review it within 2–3 business days.');
      await fetchProfile();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Submission failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statusKey = profile?.verification_status || 'NOT_SUBMITTED';
  const cfg       = STATUS_CONFIG[statusKey] || STATUS_CONFIG.NOT_SUBMITTED;
  const isApproved = statusKey === 'APPROVED';

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <GavelIcon sx={{ fontSize: 36, color: '#818cf8' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Arbitrator Profile & KYC</Typography>
          <Typography variant="body2" color="text.secondary">
            Submit your professional credentials for manual review by the admin.
          </Typography>
        </Box>
        <Chip
          icon={cfg.icon}
          label={cfg.label}
          color={cfg.color}
          sx={{ ml: 'auto', fontWeight: 700 }}
        />
      </Box>

      {/* Status banner */}
      {statusKey === 'PENDING' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          ⏳ Your profile is under review. You will receive an email once the admin verifies your credentials.
          You can update your profile and re-submit if needed.
        </Alert>
      )}
      {statusKey === 'APPROVED' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✅ Your arbitrator profile has been approved! You are now eligible to be assigned to arbitration rooms.
        </Alert>
      )}
      {statusKey === 'REJECTED' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Profile Rejected</strong>
          {profile?.admin_remarks && (
            <Box sx={{ mt: 0.5 }}>Admin remarks: {profile.admin_remarks}</Box>
          )}
          <Box sx={{ mt: 1 }}>Please update your details and re-submit.</Box>
        </Alert>
      )}

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Personal Details */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, background: cfg.bg }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <AccountCircleIcon sx={{ color: '#818cf8' }} />
          <Typography variant="subtitle1" fontWeight={700}>Personal Details</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Full Legal Name *"
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            id="arb-full-name"
          />
          <TextField
            label="Phone Number"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            id="arb-phone"
          />
          <TextField
            label="City"
            name="city"
            value={form.city}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            id="arb-city"
          />
        </Box>
      </Paper>

      {/* Professional Credentials */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <GavelIcon sx={{ color: '#818cf8' }} />
          <Typography variant="subtitle1" fontWeight={700}>Professional Credentials</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Highest Qualification *"
            name="qualification"
            value={form.qualification}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            placeholder="e.g. LLB, LLM, B.Tech, CISM"
            id="arb-qualification"
          />
          <TextField
            label="Years of Experience"
            name="years_of_experience"
            type="number"
            value={form.years_of_experience}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            InputProps={{ inputProps: { min: 0, max: 60 } }}
            id="arb-experience"
          />
          <FormControl size="small" disabled={isApproved}>
            <InputLabel>Specialization *</InputLabel>
            <Select
              name="specialization"
              value={form.specialization}
              label="Specialization *"
              onChange={handleChange}
              id="arb-specialization"
            >
              {SPECIALIZATIONS.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Bar Council Registration No."
            name="bar_council_number"
            value={form.bar_council_number}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            placeholder="e.g. DL/123/2020"
            id="arb-bar-council"
          />
          <TextField
            label="Arbitration Council / IIAM No."
            name="arbitration_council_number"
            value={form.arbitration_council_number}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            placeholder="e.g. IIAM/ARB/2021/4567"
            id="arb-council-no"
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label="Professional Bio (max 500 chars)"
            name="professional_bio"
            value={form.professional_bio}
            onChange={handleChange}
            disabled={isApproved}
            size="small"
            multiline
            rows={3}
            placeholder="Brief summary of your experience in dispute resolution, cyber law, or IT contracts…"
            inputProps={{ maxLength: 500 }}
            id="arb-bio"
            sx={{ gridColumn: '1 / -1' }}
            helperText={`${form.professional_bio.length}/500`}
          />
        </Box>
      </Paper>

      {/* Document Uploads */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <UploadFileIcon sx={{ color: '#818cf8' }} />
          <Typography variant="subtitle1" fontWeight={700}>Document Uploads</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            PDF, JPG, or PNG · Max 5 MB each
          </Typography>
        </Box>

        {[
          {
            key: 'govt_id',
            label: 'Government Photo ID *',
            hint: 'Aadhaar Card / Passport / Driving License',
            existing: profile?.govt_id_doc_key,
          },
          {
            key: 'qualification',
            label: 'Qualification Certificate *',
            hint: 'LLB / LLM / Degree Certificate',
            existing: profile?.qualification_doc_key,
          },
          {
            key: 'certification',
            label: 'Arbitration Certification (optional)',
            hint: 'IIAM / DIAC / CIArb / ICA Certificate',
            existing: profile?.certification_doc_key,
          },
        ].map(({ key, label, hint, existing }) => (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{label}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{hint}</Typography>
            {existing && !files[key] && (
              <Chip
                label="Document uploaded ✓"
                color="success"
                size="small"
                sx={{ mb: 1 }}
              />
            )}
            {!isApproved && (
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<UploadFileIcon />}
                id={`btn-upload-${key}`}
              >
                {files[key] ? files[key].name : 'Upload Document'}
                <input
                  type="file"
                  hidden
                  accept={ACCEPT}
                  onChange={(e) => handleFileChange(key, e.target.files?.[0])}
                />
              </Button>
            )}
            {fileErrors[key] && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                {fileErrors[key]}
              </Typography>
            )}
            {files[key] && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                ✓ {files[key].name} ({(files[key].size / 1024).toFixed(0)} KB)
              </Typography>
            )}
            <Divider sx={{ mt: 1.5 }} />
          </Box>
        ))}
      </Paper>

      {/* KYC Documents required notice */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, borderRadius: 2, background: 'rgba(124,58,237,0.05)', borderColor: 'rgba(124,58,237,0.2)' }}
      >
        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
          📋 Required KYC Documents for Arbitrators
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <li><strong>Government ID:</strong> Aadhaar, Passport, or Driving License (photo page)</li>
            <li><strong>Qualification Certificate:</strong> LLB/LLM degree, B.Tech, or equivalent</li>
            <li><strong>Professional Registration:</strong> Bar Council enrollment certificate (if advocate)</li>
            <li><strong>Arbitration Certification:</strong> IIAM, DIAC, CIArb or equivalent (if available)</li>
            <li><strong>Experience:</strong> Minimum 3 years in dispute resolution, cyber law, or IT contracts</li>
          </Box>
        </Typography>
        <Alert severity="info" sx={{ mt: 1.5 }}>
          All arbitrator verifications are <strong>manual</strong> — admin will review your documents and contact you via email.
          Automated API verification is not used for arbitrator profiles.
        </Alert>
      </Paper>

      {/* Submit button */}
      {!isApproved && (
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <GavelIcon />}
          id="btn-submit-arbitrator-kyc"
          sx={{ px: 4 }}
        >
          {saving ? 'Submitting…' : statusKey === 'NOT_SUBMITTED' ? 'Submit Profile for Review' : 'Update & Re-Submit'}
        </Button>
      )}
    </Box>
  );
}
